// Canonical lifecycle transport. Policy issuance crosses PHP for local booking.

import type {
  CanonicalPolicy,
  CanonicalQuote,
  DocumentType,
  ErrorClass,
  IssueRequest,
  IssueResponse,
  PaymentRequest,
  ProviderId,
  SettlementDirectory,
} from "../../types/lifecycle.ts";
import {
  isRecord,
  LifecycleResponseError,
  parseBlanks,
  parseCapabilities,
  parseCommission,
  parseDocuments,
  parseIssue,
  parsePayment,
  parseQuote,
  parseSchedule,
  validateAdvisories,
} from "./lifecycleValidation.ts";
export type * from "../../types/lifecycle.ts";

import { CALCULATOR_BASE_URL } from "../../constants/apiBases.ts";
import { COMMON_API_URL } from "../../constants/urls.ts";

const BASE = `${CALCULATOR_BASE_URL}/lifecycle`;
// Policy issue must always cross the Wing PHP boundary: insurers.php consumes
// localWrite and validates the selected local insurer before forwarding only the
// canonical request upstream.
const ISSUE_BASE = `${COMMON_API_URL}/insurers/lifecycle`;

/** The canonical error, surfaced verbatim so the UI can branch on `class`. */
export class LifecycleError extends Error {
  readonly class: ErrorClass;
  readonly code: string;
  readonly outcomeKnown: boolean;
  readonly provider: string | null;
  readonly operation: string | null;
  readonly fields: {
    field: string;
    code: string;
    message?: string;
    hint?: string;
  }[];
  readonly unmapped: { raw: string; message?: string }[];
  readonly recovery: Record<string, unknown> | null;
  readonly httpStatus: number;
  readonly issued: Partial<IssueResponse> | null;

  constructor(httpStatus: number, body: unknown) {
    const root = isRecord(body) ? body : {};
    const e = isRecord(root.error) ? root.error : {};
    super(
      typeof e.message === "string"
        ? e.message
        : `Lifecycle error (HTTP ${httpStatus})`
    );
    this.name = "LifecycleError";
    this.httpStatus = httpStatus;
    this.class = [
      "invalid_request",
      "rejected",
      "conflict",
      "not_supported",
      "unavailable",
      "indeterminate",
      "internal",
    ].includes(String(e.class))
      ? (e.class as ErrorClass)
      : "internal";
    this.code = typeof e.code === "string" ? e.code : "UNKNOWN";
    this.outcomeKnown = e.outcomeKnown === true;
    this.provider = typeof e.provider === "string" ? e.provider : null;
    this.operation = typeof e.operation === "string" ? e.operation : null;
    this.fields = Array.isArray(e.fields)
      ? e.fields.filter(isRecord).flatMap((field) =>
          typeof field.field === "string" && typeof field.code === "string"
            ? [
                {
                  field: field.field,
                  code: field.code,
                  message:
                    typeof field.message === "string"
                      ? field.message
                      : undefined,
                  hint: typeof field.hint === "string" ? field.hint : undefined,
                },
              ]
            : []
        )
      : [];
    this.unmapped = Array.isArray(e.unmapped)
      ? e.unmapped.filter(isRecord).flatMap((row) =>
          typeof row.raw === "string"
            ? [
                {
                  raw: row.raw,
                  message:
                    typeof row.message === "string" ? row.message : undefined,
                },
              ]
            : []
        )
      : [];
    this.recovery = isRecord(e.recovery) ? e.recovery : null;
    this.issued = null;
    if (isRecord(root.issued)) {
      try {
        const parsed = parseDocuments({
          policy: root.issued.policy,
          documents: [],
        });
        validateAdvisories(root.issued.advisories ?? []);
        this.issued = {
          policy: parsed.policy,
          advisories:
            (root.issued.advisories as IssueResponse["advisories"]) ?? [],
        };
      } catch {
        /* A partial error response cannot establish a policy identity. */
      }
    }
  }

  /**
   * The dangerous one: the insurer may have completed the operation even though we
   * got an error. Preserve the original request until the gateway confirms it.
   */
  get isIndeterminate(): boolean {
    return this.class === "indeterminate";
  }

  /** Safe to retry with the SAME messageId - provably nothing executed. */
  get isSafeToRetry(): boolean {
    return this.class === "unavailable" && this.outcomeKnown;
  }
}

// ─── transport ────────────────────────────────────────────────────────────────

const request = async <T>(
  jwt: string,
  path: string,
  parse: (body: unknown) => T,
  init: RequestInit = {},
  base: string = BASE
): Promise<T> => {
  const res = await fetch(`${base}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${jwt}`,
      "Content-Type": "application/json",
      ...(init.headers ?? {}),
    },
  });
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    throw new LifecycleResponseError(path);
  }
  if (!res.ok) throw new LifecycleError(res.status, body);
  return parse(body);
};

const post = <T>(
  jwt: string,
  path: string,
  payload: object,
  parse: (body: unknown) => T,
  base: string = BASE
): Promise<T> =>
  request<T>(
    jwt,
    path,
    parse,
    { method: "POST", body: JSON.stringify(payload) },
    base
  );

/** A caller-supplied id that makes issue and payment idempotent. */
export const newMessageId = (): string =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `msg-${Date.now()}-${Math.random().toString(36).slice(2)}`;

/** What the broker actually holds - spent numbers are already excluded. */
const getBlanks = (jwt: string, provider: ProviderId) =>
  request(jwt, `/blanks/${provider}`, parseBlanks);

/** EKATTE settlements grouped by област (national classifier, not per-insurer). */
const getSettlements = (jwt: string) =>
  request(jwt, "/nomenclatures/settlements", (body) => {
    if (!isRecord(body) || !Array.isArray(body.regions))
      throw new LifecycleResponseError("settlements");
    return body as unknown as SettlementDirectory;
  });

const getCapabilities = (jwt: string) =>
  request(jwt, "/capabilities", parseCapabilities).then((r) => r.providers);

/**
 * Quote EVERY insurer at once. Returns one handle for the whole run - the provider
 * is chosen afterwards, at issue, from the comparison.
 */
const getQuote = (jwt: string, quote: CanonicalQuote) =>
  post(jwt, "/quotes", { quote }, parseQuote);

const issuePolicy = (jwt: string, req: IssueRequest) =>
  post(
    jwt,
    "/policies",
    {
      ...req,
      // Also normalize saved requests created before the option was removed.
      localWrite: {
        ...req.localWrite,
        transactional:
          req.localWrite.transactional === undefined
            ? false
            : req.localWrite.transactional,
        zeroCommission: false,
      },
    },
    parseIssue,
    ISSUE_BASE
  ).then((result) => {
    if (
      result.idempotent.messageId !== req.messageId ||
      result.policy.provider !== req.provider
    )
      throw new LifecycleResponseError("issue request identity");
    return result;
  });

const getLocalBookingData = async (jwt: string, path: string) => {
  const res = await fetch(`${COMMON_API_URL}/insurers/${path}`, {
    headers: { Authorization: `Bearer ${jwt}` },
  });
  const body: unknown = await res.json().catch(() => null);
  if (!isRecord(body) || !res.ok || body.error) {
    throw new LifecycleResponseError("local booking data");
  }
  return parseCommission(body.result);
};

/** Default commission for the selected Wing company/office/agent tuple. */
const getLocalBookingCommission = (
  jwt: string,
  provider: ProviderId,
  divisionId: string,
  agentId: string,
  cid: string
) =>
  getLocalBookingData(
    jwt,
    `local-booking-commission/${provider}?divisionId=${encodeURIComponent(
      divisionId
    )}` +
      `&agentId=${encodeURIComponent(agentId)}&cid=${encodeURIComponent(cid)}`
  );

/**
 * Stable fingerprint of the canonical inputs, key order independent.
 *
 * Used to detect that the broker edited something after a premium was quoted. This
 * is a UX guard only - the gateway enforces the same rule server-side and rejects a
 * stale quote with QUOTE_INPUT_CHANGED, so a bug here cannot mis-price a customer.
 */
export const fingerprintQuote = (quote: CanonicalQuote): string => {
  const stable = (value: unknown): unknown =>
    Array.isArray(value)
      ? value.map(stable)
      : isRecord(value)
      ? Object.fromEntries(
          Object.keys(value)
            .sort()
            .map((key) => [key, stable(value[key])])
        )
      : value;
  return JSON.stringify(stable(quote));
};

/** Bring an already-issued policy into the layer. No provider traffic. */
const mintRef = (
  jwt: string,
  provider: ProviderId,
  identifiers: Record<string, string>
) =>
  post<{ policyRef: string; policy: CanonicalPolicy; verified: boolean }>(
    jwt,
    "/policies/refs",
    { provider, identifiers },
    (body) => {
      if (
        !isRecord(body) ||
        typeof body.policyRef !== "string" ||
        typeof body.verified !== "boolean"
      )
        throw new LifecycleResponseError("reference");
      return {
        policyRef: body.policyRef,
        verified: body.verified,
        policy: parseDocuments({ policy: body.policy, documents: [] }).policy,
      };
    }
  );

const getStatus = (jwt: string, policyNumber: string) =>
  post(jwt, "/policies/status", { policyNumber }, (body) => {
    if (!isRecord(body)) throw new LifecycleResponseError("status");
    return {
      policy: parseDocuments({ policy: body.policy, documents: [] }).policy,
    };
  });

const getDocumentIndex = (jwt: string, policyNumber: string) =>
  post(jwt, "/documents/index", { policyNumber }, parseDocuments);

/**
 * Download documents by policy number; document contents stay binary.
 */
const getDocument = async (
  jwt: string,
  policyNumber: string,
  documentType: DocumentType
): Promise<Blob> => {
  const res = await fetch(
    `${BASE}/documents/${documentType}?policyNumber=${encodeURIComponent(
      policyNumber
    )}`,
    { headers: { Authorization: `Bearer ${jwt}` } }
  );
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new LifecycleError(res.status, body);
  }
  return res.blob();
};

const getSchedule = (jwt: string, policyNumber: string) =>
  post(jwt, "/installments", { policyNumber }, parseSchedule);

const reportPayment = (jwt: string, req: PaymentRequest) =>
  post(jwt, "/installments/payments", req, parsePayment).then((result) => {
    if (
      result.idempotent.messageId !== req.messageId ||
      result.policy.policyNumber !== req.policyNumber
    )
      throw new LifecycleResponseError("payment request identity");
    return result;
  });

/** Decode a reference for support. Zero upstream calls; returns no key values. */
const explainRef = (jwt: string, ref: string) =>
  post(jwt, "/refs/explain", { ref }, (body) => {
    if (!isRecord(body))
      throw new LifecycleResponseError("reference explanation");
    return body;
  });

export const LifecycleService = {
  getCapabilities,
  getSettlements,
  getBlanks,
  getQuote,
  issuePolicy,
  getLocalBookingCommission,
  mintRef,
  getStatus,
  getDocumentIndex,
  getDocument,
  getSchedule,
  reportPayment,
  explainRef,
  newMessageId,
};
